/**
 * TanStack Query hooks for `admin-api`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	adminApiQueryKeys,
	createKey,
	getRateLimit,
	listAccessLog,
	listEndpoints,
	listKeys,
	revokeKey,
	type CreateKeyBody,
	type CreateKeyResponse,
	type ListAccessLogResponse,
	type ListEndpointsResponse,
	type ListKeysResponse,
	type RateLimitInfo,
	type RevokeKeyResponse,
} from "./api";

export function useAdminEndpoints(): UseQueryResult<ListEndpointsResponse, Error> {
	return useQuery({
		queryKey: adminApiQueryKeys.endpoints(),
		queryFn: listEndpoints,
	});
}

export function useAdminKeys(): UseQueryResult<ListKeysResponse, Error> {
	return useQuery({
		queryKey: adminApiQueryKeys.keys(),
		queryFn: listKeys,
	});
}

export function useAdminKeyCreate(): UseMutationResult<
	CreateKeyResponse,
	Error,
	CreateKeyBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createKey,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: adminApiQueryKeys.keys() });
		},
	});
}

export function useAdminKeyRevoke(): UseMutationResult<
	RevokeKeyResponse,
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: revokeKey,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: adminApiQueryKeys.keys() });
		},
	});
}

export function useAdminAccessLog(
	limit?: number,
): UseQueryResult<ListAccessLogResponse, Error> {
	return useQuery({
		queryKey: adminApiQueryKeys.accessLog(limit),
		queryFn: () => listAccessLog(limit),
	});
}

export function useAdminRateLimit(): UseQueryResult<RateLimitInfo, Error> {
	return useQuery({
		queryKey: adminApiQueryKeys.rateLimit(),
		queryFn: getRateLimit,
		refetchInterval: 15_000,
	});
}
